import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingBag,
  Menu,
  User,
  LogOut,
  Shield,
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ClientOnly } from "vite-react-ssg";
import { useHybridCart } from "@/contexts/HybridCartContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { LoginModal } from "./LoginModal";

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { totalItems } = useHybridCart();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FEFDFD] border-b border-primary/30 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-3 md:py-6 relative rounded-md">
          {/* Logo and Navigation */}
          <div className="flex items-center space-x-4 md:space-x-8">
            <Link to="/" className="flex items-center group">
              <img
                src="/logo.png"
                alt="Vivi con Amor y Arte"
                className="h-8 md:h-10 w-auto max-w-[160px] sm:max-w-[200px] md:max-w-none transition-all duration-300 group-hover:scale-105 group-hover:brightness-110 group-hover:drop-shadow-lg mylogo"
              />
            </Link>

            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <Link
                to="/products"
                className="text-brown hover:text-primary transition-all duration-300 relative py-2 font-medium hover:-translate-y-0.5 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
              >
                Catálogo
              </Link>
              {/* <Link
                to="/agenda-builder"
                className="text-primary hover:text-primary/80 transition-all duration-300 relative py-2 font-semibold hover:-translate-y-0.5 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left flex items-center gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Crea tu Agenda
              </Link> */}
              <Link
                to="/about"
                className="text-brown hover:text-primary transition-all duration-300 relative py-2 font-medium hover:-translate-y-0.5 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
              >
                Nosotros
              </Link>
              <Link
                to="/contact"
                className="text-brown hover:text-primary transition-all duration-300 relative py-2 font-medium hover:-translate-y-0.5 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
              >
                Contáctenos
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-brown hover:text-primary transition-all duration-300 relative py-2 font-medium hover:-translate-y-0.5 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                >
                  Admin
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Carrito Desktop */}
            <Button
              variant="ghost"
              className="hidden md:flex text-brown relative overflow-hidden transition-all duration-300 hover:scale-105 hover:bg-primary/20 group px-3 py-2 h-auto"
              onClick={() => navigate("/cart")}
            >
              <ClientOnly>
                {() => (
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <ShoppingBag className="h-6 w-6 lg:h-7 lg:w-7 transition-transform duration-300 group-hover:rotate-12" />
                      {totalItems > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-lg">
                          {totalItems > 99 ? "99+" : totalItems}
                        </span>
                      )}
                    </div>
                    <span className="hidden lg:block text-sm font-medium">
                      {totalItems > 0
                        ? `${totalItems} item${totalItems !== 1 ? "s" : ""}`
                        : "Carrito"}
                    </span>
                  </div>
                )}
              </ClientOnly>
            </Button>

            {/* Usuario/Login Desktop - Solo para usuarios autenticados o admins */}
            <div className="hidden md:flex items-center space-x-2">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-brown hover:bg-primary/20 px-3 py-2 h-auto transition-all duration-300 hover:scale-105"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="relative">
                          <User className="h-6 w-6 lg:h-7 lg:w-7" />
                          <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full h-3 w-3 border-2 border-white"></div>
                        </div>
                        <div className="hidden lg:block text-left">
                          <p className="text-sm font-medium leading-none">
                            {user?.name}
                          </p>
                          <p className="text-xs text-brown/70 leading-none mt-0.5">
                            Mi cuenta
                          </p>
                        </div>
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56 bg-white border-2 border-primary/40 shadow-lg rounded-lg !bg-white"
                  >
                    <DropdownMenuLabel>
                      <div>
                        <p className="font-medium">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/account")}>
                      Mi Cuenta
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/orders")}>
                      Mis Pedidos
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/security")}>
                      <Shield className="mr-2 h-4 w-4" />
                      Seguridad
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/admin")}>
                          Panel Admin
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                // Botón discreto solo para administradores que necesiten iniciar sesión
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsLoginModalOpen(true)}
                  className="text-brown/60 hover:text-brown hover:bg-primary/10 text-xs"
                  title="Acceso para administradores"
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Admin
                </Button>
              )}
            </div>

            {/* Mobile Menu — Sheet drawer */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="md:hidden relative group p-1"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-7 w-7 text-brown transition-all duration-300 group-hover:scale-110" />
                  <span className="absolute -inset-2 bg-primary/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300" />
                </button>
              </SheetTrigger>

              <SheetContent
                side="right"
                className="w-[80vw] max-w-xs bg-[#FEFDFD] border-l border-primary/20 p-0 flex flex-col"
              >
                {/* Sheet header con logo */}
                <div className="px-6 py-5 border-b border-primary/15">
                  <img
                    src="/logo.png"
                    alt="Vivi con Amor y Arte"
                    className="h-9 w-auto"
                  />
                </div>

                {/* Nav links */}
                <nav className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
                  <SheetClose asChild>
                    <Link
                      to="/products"
                      className="flex items-center px-4 py-3.5 text-brown hover:text-primary hover:bg-primary/10 font-medium rounded-xl transition-all duration-200"
                    >
                      Catálogo
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/about"
                      className="flex items-center px-4 py-3.5 text-brown hover:text-primary hover:bg-primary/10 font-medium rounded-xl transition-all duration-200"
                    >
                      Nosotros
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/contact"
                      className="flex items-center px-4 py-3.5 text-brown hover:text-primary hover:bg-primary/10 font-medium rounded-xl transition-all duration-200"
                    >
                      Contáctenos
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/cart"
                      className="flex items-center justify-between px-4 py-3.5 text-brown hover:text-primary hover:bg-primary/10 font-medium rounded-xl transition-all duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="h-5 w-5" />
                        Carrito
                      </div>
                      <ClientOnly>
                        {() =>
                          totalItems > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                              {totalItems > 99 ? "99+" : totalItems}
                            </span>
                          )
                        }
                      </ClientOnly>
                    </Link>
                  </SheetClose>

                  {isAuthenticated ? (
                    <div className="border-t border-primary/15 pt-3 mt-3 space-y-1">
                      <p className="px-4 py-1.5 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        Mi cuenta
                      </p>
                      <p className="px-4 py-1 text-sm font-medium text-brown-dark">
                        {user?.name}
                      </p>
                      <SheetClose asChild>
                        <Link
                          to="/orders"
                          className="flex items-center px-4 py-3.5 text-brown hover:text-primary hover:bg-primary/10 font-medium rounded-xl transition-all duration-200"
                        >
                          Mis Pedidos
                        </Link>
                      </SheetClose>
                      {isAdmin && (
                        <SheetClose asChild>
                          <Link
                            to="/admin"
                            className="flex items-center px-4 py-3.5 text-brown hover:text-primary hover:bg-primary/10 font-medium rounded-xl transition-all duration-200"
                          >
                            Panel Admin
                          </Link>
                        </SheetClose>
                      )}
                      <SheetClose asChild>
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full px-4 py-3.5 text-red-600 hover:bg-red-50 font-medium rounded-xl transition-all duration-200"
                        >
                          <LogOut className="h-5 w-5" />
                          Cerrar Sesión
                        </button>
                      </SheetClose>
                    </div>
                  ) : (
                    <div className="border-t border-primary/15 pt-3 mt-3">
                      <SheetClose asChild>
                        <button
                          className="flex items-center gap-2 w-full px-4 py-3.5 text-brown/60 hover:text-brown hover:bg-primary/10 font-medium rounded-xl transition-all duration-200 text-sm"
                          onClick={() => setIsLoginModalOpen(true)}
                        >
                          <Shield className="h-4 w-4" />
                          Acceso Admin
                        </button>
                      </SheetClose>
                    </div>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </nav>
  );
};
